import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';
import { Frame } from "scenejs";

import { EditorContext } from '../../../index';

import { toggleEditMode, unselectWidget, updateWidgetProperty } from 'actions/editor';
import { getBoundingRectForWidget } from 'components/Editor/helper/layoutManager';
import { associateFontsWithWigdet } from 'actions/fonts';
import ManageFilter from './manageFilters';
import { CreateFilter } from "components/Editor/components/properties/createFilter";
import { Property } from '../../../components/properties';
import { Font } from '../../../components/properties/fonts';
import { CONTENT, CONTENT_FILTER, CONTENT_FILTER_CONTAINER } from 'constants/widgetTypes';
import { useChildrenWidgetSelected, useParentWidgetSelectorForType, useWidgetSelected, useWidgetSelector } from 'components/Editor/hooks/useEditorSession';

import { Tab, Tabs } from "react-bootstrap";

import { FilterDropdown } from '@paperflite-marketing/cleverstory-commons';
import styles from './filters.module.scss';
import { getMediaInfo } from 'actions/pageHealth';

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

const FilterContainerWidget = React.memo(function FilterContainerWidget({ style, widgetProps, widgetRef, children, parentRef, parentProps, alignment }) {

  const dispatch = useDispatch();
  const ref = widgetRef;
  const filterRef = useRef(null);

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const [state, setState] = useState({
    active: 'regular',
    filterStyles: {},
    columnStyle: {}
  });

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const properties = _.get(widgetProps, 'properties', {});
  const deviceProperties = _.get(properties, `${breakpoint}`, {});
  const parentDeviceProperties = _.get(parentProps, `properties.${breakpoint}`, {});
  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');
  const parent = _.get(widgetProps, 'parent');
  const layout = _.get(widgetProps, `properties.${breakpoint}.layout`);
  const content = _.get(widgetProps, 'content');
  const trayAlignment = _.get(widgetProps, `properties.${breakpoint}.trayAlignment.position`);
  const parentRectLayout = getBoundingRectForWidget(editor, parentProps);

  const selected = useWidgetSelected(editor, id);
  const parentSelected = useWidgetSelected(editor, parent);
  const siblingSelection = useChildrenWidgetSelected(editor, parent);
  const childrenSelection = useChildrenWidgetSelected(editor, id);
  const filterColumnSelected = useSelector(state => {
    return _.get(state, `${editor}.present.editMode`) === id
  });
  const customFields = useSelector(state => _.get(state, 'fields.items', []));

  const horizontalColumn = _.get(parentDeviceProperties, 'horizontalColumn', false);
  const filterBoxSelectable = horizontalColumn ? filterColumnSelected : parentSelected;


  const mobileProperties = _.get(widgetProps, `properties.mobile`, {});
  const defaultDeviceProperties = _.get(widgetProps, `properties.desktop`, {});

  let filterSpacing = _.get(mobileProperties, 'padding');
  let horizontalPadding = _.get(mobileProperties, 'itemHorizontalPadding');
  let verticalPadding = _.get(mobileProperties, 'itemVerticalPadding');

  useEffect(() => {
    if(horizontalPadding === 0 && verticalPadding === 0) {
      let filterSpacing = _.get(defaultDeviceProperties, 'padding');
      let horizontalPadding = _.get(defaultDeviceProperties, 'itemHorizontalPadding');
      let verticalPadding = _.get(defaultDeviceProperties, 'itemVerticalPadding');

      let request = {
        ...mobileProperties,
        padding: filterSpacing,
        itemHorizontalPadding: horizontalPadding,
        itemVerticalPadding: verticalPadding
      }

      dispatch(updateWidgetProperty(editor, id, 'mobile', request));
    }
  }, [filterSpacing, horizontalPadding, verticalPadding]);

  useEffect(() => {
    var filterWidgetStyles = getStyles(properties, breakpoint, 'regular');
    setState(state => {
      return {
        ...state,
        filterStyles: filterWidgetStyles,
      }
    })
  }, [state.active, deviceProperties, breakpoint])

  useEffect(() => {
    if (filterColumnSelected) {
      dispatch(unselectWidget(editor, parent))
    }
  }, [filterColumnSelected])

  useEffect(() => {
      var filterColumnStyles = getColumnStyles(deviceProperties, horizontalColumn, breakpoint);

      setState(state => {
        return {
          ...state,
          columnStyle: filterColumnStyles,
        }
      })
  }, [horizontalColumn, deviceProperties, breakpoint])

  const handleColumnSelection = () => {
    if (!horizontalColumn || filterColumnSelected) {
      return;
    }

    if (parentSelected && !selected) {
      dispatch(toggleEditMode(editor, id, true))
    }
  }

  return (
    <div
      id={`filter_container_column_${id}`}
      ref={filterRef}
      onClick={handleColumnSelection}
      style={state.columnStyle}
      className={cx({
        [styles.filterColumnWrapper]: horizontalColumn,
        [styles.selectable]: parentSelected || filterColumnSelected,
        [styles.selected]: filterColumnSelected,
      })}
    >
      <FilterColumnAction
        editorSession={editor}
        breakpoint={breakpoint}
        show={(filterColumnSelected && !selected)}
        uid={id}
      />
      <div
        id={id}
        ref={ref}
        className={cx(styles.filterButtonContainer, 'widget', {
          [styles.selected]: selected,
          [styles.parentSelected]: parentSelected,
          [styles.partialSelection]: childrenSelection.selected
        })}
        data-widget-id={id}
        data-widget-type={type}
        data-selectable={filterBoxSelectable || siblingSelection.selected}
      >
        <div className={styles.filterButtonsWrapper}>
          <FilterDropdown
            filterContent={content}
            active='regular'
            alignment={alignment}
            breakpoint={breakpoint}
            widgetProps={widgetProps}
            customFields={customFields}
          />
        </div>
      </div>
    </div>
  )
})

const FilterColumnStyleEditor = ({ id, widgetProps, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const properties = _.get(widgetProps, `properties.${breakpoint}`, {});

  const alignment = _.get(properties, 'horizontalAlignment', {
    position: 'center',
    margin: breakpoint === 'desktop' ? 5 : 15
  });

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, value));
  }


  const maxHorizontalMargin = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    let filterContainer = widgets.find(item => item.uid === id && item.type === CONTENT_FILTER_CONTAINER);
    const filterColumnElement = filterContainer ? document.getElementById(`filter_container_column_${filterContainer.uid}`) : null;
    const filterElement = filterContainer ? document.getElementById(filterContainer.uid) : null;

    return filterColumnElement.clientWidth - filterElement.clientWidth;
  });

  const handleAlignmentChange = (value) => {
    const position = _.get(value, 'position');


    if (position === 'center') {
      value = {
        ...value,
        margin: 5
      }
    }

    var request = {
      ...properties,
      horizontalAlignment: {
        ...alignment,
        ...value
      }
    }

    handlePropertyChange(request);
  }

  return (
    <Property.Dialog className={styles.fontDialog}
      width={246}
      target={`#${id}`}
      title={'Horizontal Alignment'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property nameStyle={styles.propertyName} className={styles.buttonAlignmentProperty}>
          <FilterContainerHorizontalAlignment
            value={alignment}
            breakpoint={breakpoint}
            maxHorizontalMargin={maxHorizontalMargin}
            onChange={handleAlignmentChange}
          />
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
}

const FilterColumnAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const id = _.get(props, 'uid');
  const widgetProps = useWidgetSelector(editorSession, id);
  const changeView = (view) => {
    setView(view);
  }

  const closePopup = () => {
    setView(null);
  }

  return (
    <>
      <div className={cx(styles.columnSelector, { [styles.showColumnAction]: props.show })}>
        <div className={styles.searchColumnAction}>
          <Property.Action className={styles.iconButton} onClick={() => changeView('style')}>
            <i className={cx("cleverstory-customize")} />
          </Property.Action>
          <Property.Divider />
          <Property.Action className={styles.iconButton} onClick={() => { window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, '_blank') }}>
            <i className={'cleverstory-help'} />
          </Property.Action>
        </div>
        <label className={cx(styles.title)}>
          filterColumn
        </label>
      </div>
      {(view == 'style' && props.show ) &&
        <FilterColumnStyleEditor
          id={id}
          editor={editorSession}
          widgetProps={widgetProps}
          onCancel={() => {
            closePopup()
          }}
        />
      }
    </>
  )
}


const FilterContainerWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const contentWidget = useParentWidgetSelectorForType(editorSession, props.uid, CONTENT);

  const id = _.get(props, 'uid');
  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  return (
    <>
      <Property.Action className={styles.textButton} onClick={() => changeView('manage-filters')}>Manage Filters</Property.Action>
      <Property.Divider />
      <Property.Action className={styles.iconButton} onClick={() => changeView('style')}>
        <i className={cx("cleverstory-customize")} />
      </Property.Action>
      <Property.Action className={styles.iconButton} onClick={() => changeView('spacing')}>
        <i className={cx("cleverstory-spacing", styles.spacingIcon)} />
      </Property.Action>
      <Property.Action className={styles.iconButton} onClick={() => { window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, '_blank') }}>
        <i className={'cleverstory-help'} />
      </Property.Action>

      {!editMode && view === 'manage-filters' &&
        <ManageFilter
          id={id}
          editor={editorSession}
          editorContext={editorContext}
          widget={props}
          handleViewChange={changeView}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'custom-fields' &&
        <CreateFilter
          id={contentWidget.uid}
          editor={editorSession}
          editorContext={editorContext}
          widget={props}
          changeView={changeView}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'style' &&
        <FilterContainerStyleEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'spacing' &&
        <FilterSpacing
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

    </>
  );
}

const FilterContainerWidgetMobileAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const id = _.get(props, 'uid');
  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  return (
    <>
      <Property.Action className={styles.iconButton} onClick={() => changeView('spacing')}>
        <i className={cx("cleverstory-spacing", styles.spacingIcon)} />
      </Property.Action>
      <Property.Action className={styles.iconButton} onClick={() => { window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, '_blank') }}>
        <i className={'cleverstory-help'} />
      </Property.Action>

      {!editMode && view === 'spacing' &&
        <FilterSpacing
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

    </>
  );
}

const FilterContainerHorizontalAlignment = ({ value, maxHorizontalMargin, breakpoint, onChange }) => {

  const position = _.get(value, 'position') || 'center';
  const margin = _.get(value, 'margin', 0);


  const handleAlignmentChange = (position) => {
    var request = {
      position: position
    }

    if (position === 'right' || position === 'left') {
      request = {
        ...request,
        margin: breakpoint === 'desktop' ? 5 : 15
      }
    }

    onChange(request);
  }

  const handleMarginChange = (margin) => {
    var request = {
      margin: margin
    }

    onChange(request);
  }

  return (
    <div className={styles.buttonAlignment}>
      <span className={styles.subDialogName}>Alignment</span>
      <div className={styles.options}>
        <button className={cx(styles.alignment,
          {
            [styles.active]: position === 'left'
          })}
          onClick={handleAlignmentChange.bind(this, 'left')}
        >
          <i className={cx('cleverstory-widgets-align-left')}></i>
        </button>
        <button className={cx(styles.alignment,
          {
            [styles.active]: position === 'center'
          })}
          onClick={handleAlignmentChange.bind(this, 'center')}
        >
          <i className={cx('cleverstory-widgets-align-center')}></i>
        </button>
        <button className={cx(styles.alignment,
          {
            [styles.active]: position === 'right'
          })}
          onClick={handleAlignmentChange.bind(this, 'right')}
        >
          <i className={cx('cleverstory-widgets-align-right')}></i>
        </button>
      </div>


      {position !== 'center' &&
        <Property name="Margin" className={styles.marginProperty}>
          <Property.Slider
            input
            min={breakpoint === 'desktop' ? 5 : 15}
            max={maxHorizontalMargin}
            step={1}
            value={margin}
            onChange={handleMarginChange}
          />
        </Property>
      }
    </div>
  )
}


const FilterSpacing = ({ id, widget, editor, onCancel }) => {
  const dispatch = useDispatch();

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const deviceProperties = _.get(widget, `properties.${breakpoint}`, {});

  const filterSpacing = _.get(deviceProperties, 'padding');
  const horizontalPadding = _.get(deviceProperties, 'itemHorizontalPadding');
  const verticalPadding = _.get(deviceProperties, 'itemVerticalPadding');

  const handleSpacingChange = (type, value) => {
    var request = {
      ...deviceProperties,
      [type]: Number(value)
    }
    dispatch(updateWidgetProperty(editor, id, breakpoint, request));
  }

  return (
    <Property.Dialog className={styles.fontDialog}
      width={246}
      target={`#${id}`}
      title={'Spacing & Padding'}
      onCancel={onCancel}
    >
      <Property.Body>
        <Property name="Spacing Between Items" nameStyle={styles.propertyName} className={styles.filterSpacingProperty}>
          <Property.Slider
            input
            min={0}
            className={styles.customSlider}
            max={20}
            step={1}
            value={filterSpacing}
            onChange={(data) => handleSpacingChange('padding', data)}
          />
        </Property>
        <Property name="Items Padding" nameStyle={styles.propertyName} className={styles.filterSpacingProperty}>
          <span className={styles.subHeading}>Horizontal</span>
          <Property.Slider
            input
            min={8}
            className={styles.customSlider}
            max={50}
            step={1}
            value={horizontalPadding}
            onChange={(data) => handleSpacingChange('itemHorizontalPadding', data)}
          />
        </Property>
        <Property name="" className={styles.filterSpacingProperty}>
          <span className={styles.subHeading}>Vertical</span>
          <Property.Slider
            input
            min={8}
            className={styles.customSlider}
            max={50}
            step={1}
            value={verticalPadding}
            onChange={(data) => handleSpacingChange('itemVerticalPadding', data)}
          />
        </Property>
      </Property.Body>
    </Property.Dialog>
  )
}

const FilterContainerStyleEditor = ({ id, widget, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const contentFilters = _.get(widget, 'content', []);
  const properties = _.get(widget, `properties.${breakpoint}`, {});

  const alignment = _.get(properties, 'alignment', {
    position: 'center',
    margin: 5
  });

  const [state, setState] = useState({
    activeTab: 'regular'
  })

  const handleTabChange = (key) => {
    setState(() => {
      return {
        ...state,
        activeTab: key
      }
    })
  }

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, value));
  }

  const handleAlignmentChange = (value) => {
    const position = _.get(value, 'position');

    if (position === 'center') {
      value = {
        ...value,
        margin: 0
      }
    }

    var request = {
      ...properties,
      alignment: {
        ...alignment,
        ...value
      }
    }

    handlePropertyChange(request);
  }

  return (
    <Property.Dialog className={styles.customStylesDialog}
      width={246}
      target={`#${id}`}
      title={'Customize Filters'}
      onCancel={rest.onCancel}
    >
      <Property.Body className={styles.bodyStyle}>
        <Property name="Text Alignment" nameStyle={styles.propertyName} className={styles.buttonAlignmentProperty}>
          <ButtonAlignment
            value={alignment}
            onChange={handleAlignmentChange}
          />
        </Property>

        <Property name={"Dropdown Button Design"} nameStyle={styles.propertyName} className={styles.buttonDesignProperty}>
          <Tabs activeKey={state.activeTab}
            onSelect={handleTabChange}
            className={styles.buttonStyleTabs}>
            <Tab eventKey="regular" title="Regular" className={styles.buttonStyleTab}>
              <FontEditor
                editorSession={editorSession}
                id={id}
                properties={properties}
                active={'regular'}
              />
              {contentFilters && _.size(contentFilters) > 1 && 
                <DropdownSubMenuCustomize
                  editorSession={editorSession}
                  id={id}
                  properties={properties}
                  active={'regular'}
                />
              }
            </Tab>
            <Tab eventKey="hover" title="Hover" className={styles.buttonStyleTab}>
              <FontEditor
                editorSession={editorSession}
                id={id}
                properties={properties}
                active={'hover'}
              />
              {contentFilters && _.size(contentFilters) > 1 &&
                <DropdownSubMenuCustomize
                  editorSession={editorSession}
                  id={id}
                  properties={properties}
                  active={'hover'}
                /> 
              } 
            </Tab>
          </Tabs>
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
}

const DropdownSubMenuCustomize = ({ editorSession, id, properties, active }) => {
  const dispatch = useDispatch();

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const deviceProperties = _.get(properties, `${active}`, {});

  const textColor = _.get(deviceProperties, 'dropdownColor', null);
  const backgroundColor = _.get(deviceProperties, 'dropdownBackground', null);

  const border = _.get(deviceProperties, 'dropdownBorder', {});
  const borderColor = _.get(border, 'color', null);
  const borderWidth = _.get(border, 'width', 0);
  const borderRadius = _.get(border, 'radius', {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0
  });

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, value));
  }

  const handleTextColorChange = React.useCallback((color) => {
    var request = {
      ...properties,
      [active]: {
        ...properties[active],
        dropdownColor: color
      }
    }

    handlePropertyChange(request);
  }, [properties]);

  const handleBackgroundColorChange = React.useCallback((color) => {
    var request = {
      ...properties,
      [active]: {
        ...properties[active],
        dropdownBackground: color
      }
    }

    handlePropertyChange(request);
  }, [properties]);

  const handleBorderColorChange = (color) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        dropdownBorder: {
          ...border,
          color: color
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderWidthChange = (value) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        dropdownBorder: {
          ...border,
          width: value
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderRadiusChange = (radius) => {
    var request = {
      ...properties,
      [active]: {
        ...deviceProperties,
        dropdownBorder: {
          ...border,
          radius: radius
        }
      }
    }

    handlePropertyChange(request);
  }

  return (
    <Property className={styles.subMenuProperty}>
      <span className={styles.subHeading}>{`Dropdown Sub Menu Design`}</span>
      <Property.Accordion title={'Color'} className={styles.colorProperty}>
        <Property.Color title={'Text'} value={textColor} onChange={handleTextColorChange} />
        <Property.Color title={'Background'} clearable={true} value={backgroundColor} onChange={handleBackgroundColorChange} />
      </Property.Accordion>
      <Property.Accordion title={'Border'} className={styles.borderProperty}>
        <Property.Color title={'Color'} clearable={true} value={borderColor} onChange={handleBorderColorChange} />
        <>
          <Property name="Stroke Width" className={styles.buttonStyleProperty}>
            <Property.Slider
              min={0}
              max={10}
              step={1}
              input
              value={borderWidth}
              onChange={handleBorderWidthChange}
            />
          </Property>
          <Property name="Corner Radius" className={styles.buttonStyleProperty}>
            <Property.BorderRadius
              value={borderRadius}
              onChange={handleBorderRadiusChange}
            />
          </Property>
        </>
      </Property.Accordion>
    </Property>
  )
}

const FontEditor = ({ editorSession, id, properties, active }) => {
  const dispatch = useDispatch();

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const activeStyles = _.get(properties, `${active}`, {});
  const deviceProperties = _.get(properties, `${active}.filterStyles`, {});

  const textColor = _.get(deviceProperties, 'font.color', null);
  const backgroundColor = _.get(deviceProperties, 'background', null);

  const border = _.get(deviceProperties, 'border', {});
  const borderColor = _.get(border, 'color', null);
  const borderWidth = _.get(border, 'width', 0);
  const borderRadius = _.get(border, 'radius', {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0
  });

  const fontFamily = _.get(deviceProperties, 'font.fontFamily', 'Montserrat,sans-serif') || 'Montserrat,sans-serif';
  const fontSize = _.get(deviceProperties, 'font.fontSize') || 16;
  const lineSpacing = _.get(deviceProperties, 'font.lineSpacing') || 1.2;
  const letterSpacing = _.get(deviceProperties, 'font.letterSpacing') || 0;
  const fontStyle = _.get(deviceProperties, 'font.style', {});

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, value));
  }

  const handleTextColorChange = React.useCallback((color) => {
    var request = {
      ...properties,
      [active]: {
        ...activeStyles,
        filterStyles: {
          ...deviceProperties,
          font: {
            ...deviceProperties.font,
            color: color
          }
        }
      }
    }

    handlePropertyChange(request);
  }, [properties]);

  const handleBackgroundColorChange = React.useCallback((color) => {
    var request = {
      ...properties,
      [active]: {
        ...activeStyles,
        filterStyles: {
          ...deviceProperties,
          background: color
        }
      }
    }

    handlePropertyChange(request);
  }, [properties]);

  const handleBorderColorChange = (color) => {
    var request = {
      ...properties,
      [active]: {
        ...activeStyles,
        filterStyles: {
          ...deviceProperties,
          border: {
            ...border,
            color: color
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderWidthChange = (value) => {
    var request = {
      ...properties,
      [active]: {
        ...activeStyles,
        filterStyles: {
          ...deviceProperties,
          border: {
            ...border,
            width: value
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderRadiusChange = (radius) => {
    var request = {
      ...properties,
      [active]: {
        ...activeStyles,
        filterStyles: {
          ...deviceProperties,
          border: {
            ...border,
            radius: radius
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleFontFamily = (value) => {
    var font = _.get(value, 'font.id');
    var family = _.get(value, 'font.fontFamily');
    if (!family) {
      return;
    }

    var request = {
      ...properties,
      [active]: {
        ...activeStyles,
        filterStyles: {
          ...deviceProperties,
          font: {
            ...deviceProperties.font,
            fontFamily: family
          }
        }
      }
    }

    handlePropertyChange(request);
    if (font) {
      dispatch(associateFontsWithWigdet(editorSession, id, [font]));
    }
  }

  const handleSizeChange = (value) => {
    var request = {
      ...properties,
      [active]: {
        ...activeStyles,
        filterStyles: {
          ...deviceProperties,
          font: {
            ...deviceProperties.font,
            fontSize: value
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleLetterSpacingChange = (value) => {
    var request = {
      ...properties,
      [active]: {
        ...activeStyles,
        filterStyles: {
          ...deviceProperties,
          font: {
            ...deviceProperties.font,
            letterSpacing: value
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  const handleFontStyleChange = (style) => {
    var request = {
      ...properties,
      [active]: {
        ...activeStyles,
        filterStyles: {
          ...deviceProperties,
          font: {
            ...deviceProperties.font,
            style: {
              ...fontStyle,
              [style]: !fontStyle[style]
            }
          }
        }
      }
    }

    handlePropertyChange(request);
  }

  return (
    <>
      <Property.Accordion title={'Color'} className={styles.colorProperty}>
        <Property.Color title={breakpoint === 'desktop' ? 'Text' : 'Icon'} value={textColor} onChange={handleTextColorChange} />
        <Property.Color title={'Background'} clearable={true} value={backgroundColor} onChange={handleBackgroundColorChange} />
      </Property.Accordion>

      <Property.Accordion title={'Border'} className={styles.borderProperty}>
        <Property.Color title={'Color'} clearable={true} value={borderColor} onChange={handleBorderColorChange} />
        <Property name="Stroke Width" className={styles.buttonStyleProperty}>
          <Property.Slider
            min={0}
            max={10}
            step={1}
            input
            value={borderWidth}
            onChange={handleBorderWidthChange}
          />
        </Property>
        <Property name="Corner Radius" className={styles.buttonStyleProperty}>
          <Property.BorderRadius
            value={borderRadius}
            onChange={handleBorderRadiusChange}
          />
        </Property>

      </Property.Accordion>
      {active === 'regular' &&
        <Property.Accordion title={'Text'} className={styles.textProperty}>
          <Font.Family
            value={fontFamily}
            onChange={handleFontFamily}
            className={styles.buttonStyleProperty}
          />
          <Font.Size
            value={fontSize}
            min={8}
            max={37}
            onChange={handleSizeChange}
            className={styles.buttonStyleProperty}
          />

          <Property className={styles.buttonStyleProperty}>
            <div className={styles.fontActions}>
              <Property.Action className={cx(styles.iconButton, { [styles.active]: _.get(fontStyle, 'bold', false) })}
                onClick={handleFontStyleChange.bind(this, 'bold')}
              >
                <i className={'cleverstory-bold'} />
              </Property.Action>
              <Property.Action className={cx(styles.iconButton, { [styles.active]: _.get(fontStyle, 'italic', false) })}
                onClick={handleFontStyleChange.bind(this, 'italic')}
              >
                <i className={'cleverstory-italics'} />
              </Property.Action>
              <Property.Action className={cx(styles.iconButton, { [styles.active]: _.get(fontStyle, 'underline', false) })}
                onClick={handleFontStyleChange.bind(this, 'underline')}
              >
                <i className={'cleverstory-underline'} />
              </Property.Action>
            </div>
          </Property>
          <Property name="Character Spacing" className={styles.characterSpacingProperty}>
            <Property.Slider
              input
              min={-0.1}
              max={0.7}
              step={0.05}
              value={letterSpacing}
              onChange={handleLetterSpacingChange}>
            </Property.Slider>
          </Property>
        </Property.Accordion>
      }
    </>
  )
}

const ButtonAlignment = ({ value, onChange }) => {
  const position = _.get(value, 'position') || 'center';
  const margin = _.get(value, 'margin', 0) || 0;

  const handleAlignmentChange = (position) => {
    var request = {
      position: position,
      margin: margin
    }

    onChange(request);
  }

  const handleMarginChange = (margin) => {
    var request = {
      margin: margin
    }

    onChange(request);
  }

  return (
    <div className={styles.buttonAlignment}>
      <div className={styles.options}>
        <button className={cx(styles.alignment,
          {
            [styles.active]: position === 'left'
          })}
          onClick={handleAlignmentChange.bind(this, 'left')}
        >
          <i className={cx('cleverstory-align-left')}></i>
        </button>
        <button className={cx(styles.alignment,
          {
            [styles.active]: position === 'center'
          })}
          onClick={handleAlignmentChange.bind(this, 'center')}
        >
          <i className={cx('cleverstory-align-center')}></i>
        </button>
        <button className={cx(styles.alignment,
          {
            [styles.active]: position === 'right'
          })}
          onClick={handleAlignmentChange.bind(this, 'right')}
        >
          <i className={cx('cleverstory-align-right')}></i>
        </button>
      </div>

      {position !== 'center' &&
        <Property name="Margin" className={styles.marginProperty}>
          <Property.Slider
            min={0}
            input
            max={100}
            step={1}
            value={margin}
            onChange={handleMarginChange}
          />
        </Property>
      }
    </div>
  )
}

FilterContainerWidget.actions = (editor, breakpoint, props) => {

  return (
    <>
      {breakpoint === 'desktop' &&
        <FilterContainerWidgetDesktopAction editorSession={editor} breakpoint={breakpoint} {...props} />
      }
      {breakpoint === 'mobile' &&
        <FilterContainerWidgetMobileAction editorSession={editor} breakpoint={breakpoint} {...props} />
      }
    </>
  );
}

FilterContainerWidget.sizeConfig = async (widget, mediaCache) => {
  const url = _.get(widget, 'properties.desktop.background.media.url', '');
  if (!url) {
    return {
      size: 0,
      type: 'image'
    };
  }
  return {
    size:  await getMediaInfo(`${IMAGE_CDN_ENDPOINT}/${url}`, mediaCache) || 0, // MB
    type: 'image'
  };
}

FilterContainerWidget.moveableConfig = (editorSession, widget, breakpoint) => {
  const widgetElement = document.querySelector(`#${editorSession} .editor_widget_renderer #${widget.uid}`);
  const filterSectionElement = widgetElement?.closest(`div[data-widget-type="${CONTENT_FILTER}"]`);
  const pageElement = widgetElement?.closest(`.editor_widget_scroll_container`);

  const rect = filterSectionElement?.getBoundingClientRect();
  const parentRect = pageElement?.getBoundingClientRect();

  const left = rect?.left - parentRect?.left;
  const top = pageElement?.scrollTop + rect?.top - parentRect?.top;


  const bounds = {
    left: left,
    right: left + rect?.width,
    top: top,
    bottom: top + rect?.height
  }

  return {
    draggable: false,
    resizable: false,
    rotatable: false,
    selectable: true,
    cloneable: false,
    deletable: false,
    renderDirections: [],
    bounds: bounds,
    onReparent: (widget, parent) => {
      return false
    },
    onResize: (widget, layout, breakpoint) => {
      const frame = new Frame(layout);
      return frame.get();
    }
  }
}

/**
 * Generate Styles
 * 
*/
function getStyles(properties, breakpoint, active) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = _.get(properties, `${breakpoint}`);
  const deviceProperties = _.get(properties, `${breakpoint}`);
  const defaultStyles = _.get(defaultProperties, `regular.filterStyles`, {});

  //font-color
  const colorRgb = _.get(defaultProperties, `${active}.filterStyles.font.color`, null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    ...color
  }

  //background
  const backgroundRgb = _.get(defaultProperties, `${active}.filterStyles.background`, null);
  var background = 'inherit';
  if (backgroundRgb) {
    background = `rgba(${backgroundRgb.r}, ${backgroundRgb.g}, ${backgroundRgb.b}, ${backgroundRgb.a})`;
  }

  styles['backgroundColor'] = background;

  const font = _.get(defaultStyles, 'font', {});

  //font-family
  const fontFamily = _.get(font, 'fontFamily');
  if (fontFamily) {
    styles = {
      ...styles,
      fontFamily: fontFamily
    }
  }

  //font-size
  const fontSizeString = _.get(deviceProperties, 'regular.filterStyles.font.fontSize', null);
  var fontSize = 'inherit'
  if (fontSizeString) {
    fontSize = parseInt(fontSizeString) + 'px';
  }

  styles['fontSize'] = fontSize;

  //line spacing
  const lineSpacingString = _.get(deviceProperties, 'regular.filterStyles.font.lineSpacing', null);
  if (lineSpacingString) {
    const lineSpacing = parseFloat(lineSpacingString) + 'em';
    styles['lineHeight'] = lineSpacing;
  }

  //Letter Spacing
  const letterSpacingString = _.get(deviceProperties, 'regular.filterStyles.font.letterSpacing', null);
  if (letterSpacingString) {
    const letterSpacing = parseFloat(letterSpacingString) + 'em';
    styles['letterSpacing'] = letterSpacing;
  }

  //line spacing 
  const layoutHeight = _.get(deviceProperties, 'layout.height');
  if (layoutHeight && breakpoint === 'desktop') {
    styles['lineHeight'] = layoutHeight;
  }

  //font-style
  const bold = _.get(font, 'style.bold', false);
  if (bold) {
    styles['fontWeight'] = 'bold';
  }

  const italic = _.get(font, 'style.italic', false);
  if (italic) {
    styles['fontStyle'] = 'italic';
  }

  const underline = _.get(font, 'style.underline', false);
  if (underline) {
    styles['textDecoration'] = 'underline';
  }

  //border
  const border = _.get(defaultStyles, 'border', {});
  const borderStyle = getBorderStyles(border);
  styles = {
    ...styles,
    ...borderStyle
  }

  // spacing 
  const spacing = _.get(deviceProperties, 'padding');
  const verticalPadding = _.get(deviceProperties, 'itemVerticalPadding');
  const horizontalPadding = _.get(deviceProperties, 'itemHorizontalPadding');

  styles = {
    ...styles,
    paddingLeft: `${horizontalPadding}px`,
    paddingRight: `${horizontalPadding}px`,
    paddingTop: `${verticalPadding}px`,
    paddingBottom: `${verticalPadding}px`
  }

  if (breakpoint === 'mobile') {
    styles = {
      ...styles,
      marginBottom: `${spacing}px`,
    }
  } else if (breakpoint === 'desktop') {
    styles = {
      ...styles,
      marginRight: `${spacing}px`,
    }
  }

  //alignment & padding
  const alignment = _.get(deviceProperties, 'alignment', {});
  var position = _.get(alignment, 'position', 'center');

  styles['justifyContent'] = position;
  styles['textAlign'] = position;

  var margin = _.get(alignment, 'margin', 0) || 0;
  if (position === 'left') {
    styles = {
      ...styles,
      paddingLeft: `${margin}px`
    }
  } else if (position === 'right') {
    styles = {
      ...styles,
      paddingRight: `${margin}px`
    }
  }

  return styles;
}

/**
 * Generate  column Styles
 * 
*/
function getColumnStyles(properties, horizontalColumn, breakpoint) {
  var containerStyles = {};

  if (!properties) {
    return containerStyles;
  }
  const columnAlignment = _.get(properties, `horizontalAlignment`, {
    position: 'center',
    margin: 0
  });


  var alignmentPosition = _.get(columnAlignment, 'position', 'center');
  var alignmnetMargin = _.get(columnAlignment, 'margin', 0);

  //alignment
  containerStyles['justifyContent'] = alignmentPosition;

  if(!horizontalColumn) {
    return containerStyles;
  }

  if (alignmentPosition === 'left') {
    containerStyles = {
      ...containerStyles,
      justifyContent: 'flex-start',
      paddingLeft: `${alignmnetMargin}px`
    }
  }
  else if (alignmentPosition === 'right') {
    containerStyles = {
      ...containerStyles,
      justifyContent: 'flex-end',
      paddingRight: `${alignmnetMargin}px`
    }
  }

  return containerStyles;
}


export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = 'inherit';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  var styles = {
    color: color
  };

  return styles;
}

export function getBorderStyles(value) {
  if (!value) {
    return null;
  }

  var styles = {};
  const topLeft = _.get(value, 'radius.topLeft', null);
  const topRight = _.get(value, 'radius.topRight', null);
  const bottomLeft = _.get(value, 'radius.bottomLeft', null);
  const bottomRight = _.get(value, 'radius.bottomRight', null);

  if (topLeft) {
    styles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    styles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  const colorRgb = _.get(value, 'color');

  var color = 'transparent';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  styles['borderColor'] = color;

  const borderWidthString = _.get(value, 'width', null);
  var borderWidget = parseInt(borderWidthString) + 'px';

  styles['borderWidth'] = borderWidget;

  return styles;
}



export { FilterContainerWidget };